import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Building2, Bed, Bath, Maximize, ChevronUp, ChevronDown, Image as ImageIcon, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent } from '@/components/ui/dialog';

interface ProjectOverviewProps {
  project: any;
  developerName: string;
  formatPrice: (min: number | null, max: number | null, currency: string) => string;
}

export function ProjectOverview({ project, developerName, formatPrice }: ProjectOverviewProps) {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [floorPlanPreview, setFloorPlanPreview] = useState<{ urls: string[]; index: number; name: string } | null>(null);

  const { data: unitTypes, isLoading } = useQuery({
    queryKey: ['project-unit-types', project.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_unit_types' as any)
        .select('*')
        .eq('project_id', project.id)
        .order('sort_order');
      if (error) throw error;
      return data as any[];
    },
  });

  const getFloorPlans = (unit: any): string[] => {
    if (Array.isArray(unit.floor_plan_urls) && unit.floor_plan_urls.length > 0) return unit.floor_plan_urls;
    if (unit.floor_plan_url) return [unit.floor_plan_url];
    return [];
  };

  // Auto-expand first unit that has floor plans (or first unit) when data loads
  useEffect(() => {
    if (!unitTypes || unitTypes.length === 0 || expandedCategory) return;
    const firstWithPlans = unitTypes.find((u) => getFloorPlans(u).length > 0);
    setExpandedCategory((firstWithPlans || unitTypes[0]).id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitTypes]);

  const fmtArea = (min: number | null, max: number | null) => {
    if (min && max) return `${min} - ${max} m²`;
    if (min) return `${min} m²`;
    if (max) return `${max} m²`;
    return '—';
  };


  const fmtPrice = (min: number | null, max: number | null, cur: string) => {
    const c = cur === 'USD' ? '$' : 'Br';
    const fmt = (n: number) => n >= 1000000 ? `${(n / 1000000).toFixed(2).replace(/\.?0+$/, '')}M` : n >= 1000 ? `${(n / 1000).toFixed(0)}K` : n.toLocaleString('en-US');
    if (min && max) return `${c} ${fmt(min)} – ${c} ${fmt(max)}`;
    if (min) return `From ${c} ${fmt(min)}`;
    if (max) return `Up to ${c} ${fmt(max)}`;
    return 'Price TBD';
  };

  const hasUnits = unitTypes && unitTypes.length > 0;


  const closePreview = useCallback(() => setFloorPlanPreview(null), []);
  const goPrev = useCallback(() => {
    setFloorPlanPreview(p => p ? { ...p, index: (p.index - 1 + p.urls.length) % p.urls.length } : p);
  }, []);
  const goNext = useCallback(() => {
    setFloorPlanPreview(p => p ? { ...p, index: (p.index + 1) % p.urls.length } : p);
  }, []);

  useEffect(() => {
    if (!floorPlanPreview) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'Escape') closePreview();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [floorPlanPreview, goPrev, goNext, closePreview]);

  return (
    <section>
      <h2 className="font-display text-lg md:text-xl font-bold mb-3 md:mb-4">Overview</h2>
      <div className="grid grid-cols-2 md:flex md:flex-wrap gap-3 md:gap-8 mb-4 md:mb-6 text-sm">
        <div className="flex items-start gap-2 min-w-0">
          <Building2 className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="text-xs text-muted-foreground">Offering</div>
            <div className="font-semibold text-sm break-words">{hasUnits ? `${unitTypes.length} Unit Types` : 'Apartments'}{project.total_units ? `, ${project.total_units} Units` : ''}</div>
          </div>
        </div>
        <div className="flex items-start gap-2 min-w-0">
          <Building2 className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="text-xs text-muted-foreground">Developer</div>
            <div className="font-semibold text-sm break-words">{developerName}</div>
          </div>
        </div>
      </div>

      {isLoading && <Skeleton className="h-32 rounded-xl" />}

      {hasUnits && (
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-display text-base md:text-lg font-bold">Unit Types & Layouts</h3>
            <span className="text-xs text-muted-foreground">{unitTypes.length} {unitTypes.length === 1 ? 'type' : 'types'}</span>
          </div>
          {unitTypes.map((unit: any) => {
            const floorPlans = getFloorPlans(unit);
            const isOpen = expandedCategory === unit.id;
            return (
              <div key={unit.id}>
                <button
                  onClick={() => setExpandedCategory(isOpen ? null : unit.id)}
                  className="w-full flex items-center justify-between gap-2 p-3 md:p-4 bg-primary/5 rounded-xl hover:bg-primary/10 transition-colors"
                >
                  <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
                    <Building2 className="h-5 w-5 text-primary shrink-0" />
                    <span className="font-bold text-sm md:text-base truncate">{unit.name}</span>
                    {floorPlans.length > 0 && (
                      <span className="hidden sm:inline-flex items-center gap-1 text-[10px] md:text-xs font-medium text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full shrink-0">
                        <ImageIcon className="h-3 w-3" />
                        {floorPlans.length} {floorPlans.length === 1 ? 'layout' : 'layouts'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {floorPlans.length > 0 && (
                      <span className="sm:hidden inline-flex items-center text-[10px] font-medium text-primary bg-primary/10 border border-primary/20 px-1.5 py-0.5 rounded-full">
                        {floorPlans.length}
                      </span>
                    )}
                    <span className="font-bold text-xs md:text-sm whitespace-nowrap">{fmtPrice(unit.price_min, unit.price_max, unit.currency || 'ETB')}</span>
                    {isOpen ? <ChevronUp className="h-4 w-4 text-primary" /> : <ChevronDown className="h-4 w-4 text-primary" />}
                  </div>
                </button>
                {expandedCategory === unit.id && (
                  <div className="border rounded-xl p-4 mt-2 space-y-4">
                    <div className="flex flex-wrap gap-4 md:gap-6 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Maximize className="h-4 w-4 text-primary/70" />
                        <div>
                          <div className="text-xs">Area Size</div>
                          <div className="font-medium text-foreground">{fmtArea(unit.area_min, unit.area_max)}</div>
                        </div>
                      </div>
                      {unit.bedrooms != null && (
                        <div className="flex items-center gap-2">
                          <Bed className="h-4 w-4 text-primary/70" />
                          <div>
                            <div className="text-xs">Bedrooms</div>
                            <div className="font-medium text-foreground">{unit.bedrooms}</div>
                          </div>
                        </div>
                      )}
                      {unit.bathrooms != null && (
                        <div className="flex items-center gap-2">
                          <Bath className="h-4 w-4 text-primary/70" />
                          <div>
                            <div className="text-xs">Bathrooms</div>
                            <div className="font-medium text-foreground">{unit.bathrooms}</div>
                          </div>
                        </div>
                      )}
                    </div>
                    {floorPlans.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-foreground flex items-center gap-1.5 mb-2">
                          <ImageIcon className="h-3.5 w-3.5 text-primary" />
                          {floorPlans.length === 1 ? 'Floor Plan' : `${floorPlans.length} Floor Plans / Layouts`}
                          <span className="text-[10px] font-normal text-muted-foreground ml-1">— click to enlarge</span>
                        </div>
                        <div className="flex gap-3 overflow-x-auto pb-2 sm:grid sm:grid-cols-3 md:grid-cols-4 sm:overflow-visible">
                          {floorPlans.map((url, k) => (
                            <button
                              key={k}
                              type="button"
                              onClick={() => setFloorPlanPreview({ urls: floorPlans, index: k, name: unit.name })}
                              className="shrink-0 sm:shrink group relative border-2 border-border hover:border-primary rounded-lg overflow-hidden transition-all hover:shadow-md text-left bg-muted/30"
                            >
                              <img
                                src={url}
                                alt={`${unit.name} floor plan ${k + 1}`}
                                className="h-28 w-36 sm:h-32 sm:w-full object-contain p-1"
                                loading="lazy"
                              />
                              <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between">
                                <span className="text-[10px] font-semibold text-foreground bg-background/90 px-1.5 py-0.5 rounded shadow-sm">
                                  Plan {k + 1}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isLoading && !hasUnits && (
        <p className="text-sm text-muted-foreground">No unit details available yet.</p>
      )}

      <Dialog open={!!floorPlanPreview} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          {floorPlanPreview && (
            <div className="relative">
              <button
                onClick={closePreview}
                aria-label="Close"
                className="absolute top-3 right-3 z-10 bg-background/80 hover:bg-background rounded-full p-2 shadow"
              >
                <X className="h-4 w-4" />
              </button>
              {floorPlanPreview.urls.length > 1 && (
                <>
                  <button
                    onClick={goPrev}
                    aria-label="Previous floor plan"
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background rounded-full p-2 shadow"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={goNext}
                    aria-label="Next floor plan"
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background rounded-full p-2 shadow"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
              <div className="bg-muted/30 flex items-center justify-center p-4 max-h-[85vh]">
                <img
                  src={floorPlanPreview.urls[floorPlanPreview.index]}
                  alt={`${floorPlanPreview.name} floor plan ${floorPlanPreview.index + 1}`}
                  className="max-w-full max-h-[78vh] object-contain"
                />
              </div>
              <div className="px-4 py-3 border-t text-sm font-medium flex items-center justify-between">
                <span>{floorPlanPreview.name} — Floor Plan</span>
                {floorPlanPreview.urls.length > 1 && (
                  <span className="text-xs text-muted-foreground">
                    {floorPlanPreview.index + 1} / {floorPlanPreview.urls.length}
                  </span>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
